import { inject } from '@angular/core';

// 3D Party libs
import { jwtDecode } from 'jwt-decode';

// Models
import {
  PropertyModel,
  OrganizationModel,
  UserModel,
  BuildingModel,
} from './core';

// CASL
import { unpackRules } from '@casl/ability/extra';
import {
  AbilityClass,
  createMongoAbility,
  ExtractSubjectType,
  MongoAbility,
  MongoQuery,
  PureAbility,
  SubjectRawRule,
} from '@casl/ability';

// Services
import { AuthService } from './core/services/auth.service';

type Actions = 'create' | 'read' | 'update' | 'delete' | 'manage';
type Subjects =
  | 'organization'
  | 'property'
  | 'user'
  | 'dashboard'
  | 'dashboard-sales'
  | 'building'
  | 'smart-lock-unlock-record'
  | 'pms-integration-webhook'
  | PropertyModel
  | OrganizationModel
  | UserModel
  | BuildingModel
  | 'ProviderManagerServiceRequest'
  | 'service';

export type AppAbility = MongoAbility<[Actions, Subjects]>;
export const AppAbility = PureAbility as AbilityClass<AppAbility>;

/**
 * Defines the abilities for the application.
 * This function retrieves the token from the `AuthService`, decodes it using `jwtDecode`,
 * and creates a Mongo ability based on the decoded rules.
 *
 * @returns The created Mongo ability.
 */
export function defineAbilitiesFor() {
  const authService = inject(AuthService);

  const token = authService.token;
  
  // Check if token exists and is valid (JWT tokens have 3 parts separated by dots)
  if (!token || typeof token !== 'string' || token.trim() === '' || token.split('.').length !== 3) {
    // Return an empty ability if token is not available or invalid
    return createMongoAbility([]);
  }

  try {
    const data: any = jwtDecode(token);
    return createMongoAbility(
      unpackRules(data.rules) as SubjectRawRule<string, ExtractSubjectType<Subjects>, MongoQuery>[],
    );
  } catch (error) {
    // If decoding fails, return an empty ability
    console.warn('Failed to decode JWT token for abilities:', error);
    return createMongoAbility([]);
  }
}
